<template>
    <div>
        <!-- Alert when no field name is provided -->
        <span v-if="!name">Field name required for field to function</span>

        <!-- Vee-validate Field Wrapper -->
        <Field
            v-model="localField"
            v-if="name"
            :name="name"
            :rules="rules"
            v-slot="{ field, errorMessage }"
            :keep-value="true"
        >
            <!-- Field Label and Description -->
            <div class="mb-1">
                <div class="flex gap-4">
                    <label
                        v-if="label && canShowLabel"
                        :class="labelClass"
                        :for="name"
                        class="font-bold!"
                    >
                        {{ label }}

                        <span v-if="isFieldRequired" class="text-red-500"
                            >*</span
                        >
                    </label>
                    <GenerateAI
                        v-if="enableAi"
                        :data="localField"
                        @useContent="handleUseAiContent"
                    />
                </div>
                <p
                    v-if="description && canShowDescription"
                    class="text-xs text-gray-600 mb-2!"
                >
                    {{ description }}
                </p>
            </div>

            <!-- Textarea Field -->
            <textarea
                v-if="isTextarea"
                :placeholder="placeholder"
                :style="{ height: height + 'px' }"
                v-bind="{ ...field, ...attrs }"
                class="input"
                :class="[{ 'outline-2! outline-red-500!': errorMessage }]"
            ></textarea>

            <!-- File Upload Field -->
            <div v-if="isFile">
                <FileUpload
                    v-model="localField"
                    :name="name"
                    :multiple="multiple"
                    :error-message="errorMessage"
                    allowed-file-types=""
                />
            </div>

            <!-- Select Field -->
            <div v-if="isSelect" class="relative">
                <select
                    :id="name"
                    :name="name"
                    v-model="localField"
                    :class="[
                        'block w-full appearance-none bg-white border border-gray-300 hover:border-gray-400 px-4 py-2 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline placeholder:text-gray-800! font-bold',
                        errorMessage ? '!outline-red-500 !outline-2' : '',
                        !localField ? 'text-gray-800!' : '',
                        selectClass,
                    ]"
                >
                    <option :value="null" disabled>Select an option</option>
                    <template v-for="(option, index) in options" :key="index">
                        <option :value="option.value">
                            {{ option.label }}
                        </option>
                    </template>
                </select>

                <!-- dropdown caret -->
                <div
                    class="pointer-events-none absolute inset-y-0 right-2 flex items-center px-2 text-gray-600 text-base"
                >
                    <div class="size-4 max-w-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-full h-full object-cover"
                            viewBox="0 0 448 512"
                        >
                            <path
                                d="M212.7 363.3c6.2 6.2 16.4 6.2 22.6 0l160-160c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 329.4 75.3 180.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l160 160z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <!-- Single Checkbox Field -->
            <div v-if="isSingleCheckbox">
                <label class="flex gap-3 cursor-pointer select-none">
                    <div class="flex h-6 shrink-0 items-center">
                        <div class="group grid size-4 grid-cols-1">
                            <input
                                :id="name"
                                aria-describedby="comments-description"
                                :name="name"
                                type="checkbox"
                                v-model="localField"
                                class="col-start-1 row-start-1 cursor-pointer appearance-none rounded-sm border border-gray-300 bg-white checked:border-black checked:bg-black indeterminate:border-black indeterminate:bg-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <svg
                                class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <path
                                    class="opacity-0 group-has-checked:opacity-100"
                                    d="M3 8L6 11L11 3.5"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    class="opacity-0 group-has-indeterminate:opacity-100"
                                    d="M3 7H11"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                    <div class="text-sm/6">
                        <span class="font-medium text-gray-900">{{
                            label
                        }}</span>
                        <p class="text-gray-500 text-xs">
                            {{ description }}
                        </p>
                    </div>
                </label>
            </div>

            <!-- Checkbox Group Field -->
            <div
                class="flex flex-col justify-between gap-2"
                v-if="isCheckbox"
                :class="{
                    '!outline-red-500 p-2 !outline-2 rounded-xl': errorMessage,
                }"
            >
                <template v-for="(option, index) in options" :key="index">
                    <div class="flex items-center cursor-pointer">
                        <div class="group grid size-4 grid-cols-1">
                            <input
                                :id="`${name}-${index}`"
                                :name="name"
                                v-model="localField"
                                :value="option.value"
                                type="checkbox"
                                :class="{
                                    '!outline-red-500 !outline-2': errorMessage,
                                }"
                                class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-primary-600 checked:bg-primary-600 indeterminate:border-primary-600 indeterminate:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto select-none"
                            />
                            <svg
                                class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <path
                                    class="opacity-0 group-has-checked:opacity-100"
                                    d="M3 8L6 11L11 3.5"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    class="opacity-0 group-has-indeterminate:opacity-100"
                                    d="M3 7H11"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <label
                            :for="`${name}-${index}`"
                            class="ml-2 cursor-pointer select-none"
                        >
                            {{ option.label }}
                        </label>
                    </div>
                </template>
            </div>

            <!-- Radio Group Field -->
            <div
                class="flex flex-col md:flex-row gap-3 md:gap-3"
                v-if="isRadio"
                :class="{
                    '!outline-red-500 p-2 !outline-2 rounded-xl': errorMessage,
                    'flex-col! gap-2!': stacked,
                }"
            >
                <template v-for="(option, index) in options" :key="index">
                    <label
                        :for="`${name}-${index}`"
                        class="flex items-center cursor-pointer hover:text-black transition-all duration-300 select-none border p-2 rounded-xl pr-4 pl-2"
                        :class="{ 'text-black': localField === option.value }"
                    >
                        <input
                            :id="`${name}-${index}`"
                            v-model="localField"
                            :name="name"
                            :value="option.value"
                            type="radio"
                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-black checked:bg-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                        />
                        <span class="ml-2">{{ option.label }}</span>
                    </label>
                </template>
            </div>

            <!-- Consent Field -->
            <div v-if="isConsent" class="flex items-center gap-2">
                <div class="group grid size-4 grid-cols-1">
                    <input
                        :id="name"
                        v-model="localField"
                        :name="name"
                        type="checkbox"
                        class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-primary-600 checked:bg-primary-600 indeterminate:border-primary-600 indeterminate:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                    />
                    <svg
                        class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                        viewBox="0 0 14 14"
                        fill="none"
                    >
                        <path
                            class="opacity-0 group-has-checked:opacity-100"
                            d="M3 8L6 11L11 3.5"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            class="opacity-0 group-has-indeterminate:opacity-100"
                            d="M3 7H11"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <label :for="name" class="cursor-pointer">
                    {{ label }}
                </label>
            </div>

            <!-- Signature Pad Field -->
            <SignaturePadField
                v-model="localField"
                v-if="isSignature"
                :name="name"
            />

            <!-- Input Switch Field -->
            <InputSwitch v-model="localField" v-if="isSwitch" :name="name" />

            <!-- Standard Input Field (text, email, etc.) -->
            <input
                v-if="isInput || isTel"
                ref="input"
                class="input"
                :placeholder="placeholder"
                v-bind="{ ...field, ...attrs }"
                :class="[
                    { '!outline-red-500 !outline-2': errorMessage },
                    fieldClass,
                ]"
            />

            <!-- Error Message -->
            <span
                v-if="errorMessage"
                class="text-red-500 text-xs font-semibold font-light"
            >
                {{ customErrorMessage ?? errorMessage }}
            </span>
        </Field>
    </div>
</template>

<script setup>
import { ref, watch, computed, useAttrs, defineAsyncComponent } from "vue";
import { Field } from "vee-validate";

// Asynchronously load components
const SignaturePadField = defineAsyncComponent(() =>
    import("@helpers/Forms/SignaturePadField.vue")
);
const InputSwitch = defineAsyncComponent(() =>
    import("@helpers/Forms/InputSwitch.vue")
);
const FileUpload = defineAsyncComponent(() =>
    import("@helpers/Forms/FileUpload.vue")
);
const GenerateAI = defineAsyncComponent(() =>
    import("@helpers/AI/GenerateAi.vue")
);

// Define component props
const props = defineProps({
    label: { type: String },
    labelClass: { type: String, default: "labelClass" },
    description: { type: String },
    selectClass: { type: String, default: "selectClass" },
    name: { type: String, required: true },
    rules: { type: [String, Object] },
    options: { type: [Array, Object], default: () => [] },
    modelValue: { type: [String, Number, Boolean, Object] },
    height: { type: Number, default: 100 },
    placeholder: { type: String, default: "" },
    trackBy: { type: String, default: "id" },
    trackLabel: { type: String, default: "id" },
    fieldClass: { type: String, default: "input" },
    clearOnClick: { type: Boolean, default: false },
    mode: { type: String, default: "international" },
    multiple: { type: Boolean, default: true },
    allowedFileTypes: { type: String, default: "" },
    maxUpload: { type: Number, default: 0 },
    customErrorMessage: { type: String },
    stacked: { type: Boolean, default: false },
    enableAi: { type: Boolean, default: false },
});

const attrs = useAttrs();
const emit = defineEmits(["update:modelValue"]);
const localField = ref(props.modelValue);

// Reactive file error message
const fileError = ref("");

// Compute allowed file types array from comma-separated string
const allowedFileTypesArray = computed(() => {
    if (!props.allowedFileTypes) return [];
    return props.allowedFileTypes
        .split(",")
        .map((type) => type.trim().toLowerCase())
        .filter((type) => type);
});

// Field type computed properties
const isInput = computed(() => {
    const allowedTypes = [
        "text",
        "email",
        "password",
        "number",
        "hidden",
        "date",
        "datetime-local",
        "time",
    ];
    return allowedTypes.includes(attrs.type);
});
const isTextarea = computed(() => attrs.type === "textarea");
const isTel = computed(() => attrs.type === "tel");
const isSignature = computed(() => attrs.type === "signature");
const isCheckbox = computed(() => attrs.type === "checkbox");
const isSingleCheckbox = computed(() => attrs.type === "single-checkbox");
const isSelect = computed(() => attrs.type === "select");
const isRadio = computed(() => attrs.type === "radio");
const isFile = computed(() => attrs.type === "file");
const isConsent = computed(() => attrs.type === "consent");
const isSwitch = computed(() => attrs.type === "switch");

// Sync localField with modelValue prop
watch(
    () => props.modelValue,
    (newValue) => {
        localField.value = newValue;
    }
);
watch(localField, (newValue) => {
    emit("update:modelValue", newValue);
    fileError.value = "";
});

// Computed array of selected files
const filesArray = computed(() => {
    if (!localField.value) return [];
    if (localField.value instanceof FileList)
        return Array.from(localField.value);
    if (Array.isArray(localField.value)) return localField.value;
    return [];
});

// File size formatter
function formatFileSize(size) {
    if (size < 1024) return size + " bytes";
    else if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
    else return (size / (1024 * 1024)).toFixed(1) + " MB";
}

// File input and drag/drop handlers
const fileInput = ref(null);
const isDragging = ref(false);
function triggerFileInput() {
    fileInput.value.click();
}
function onDragOver() {
    isDragging.value = true;
}
function onDragLeave() {
    isDragging.value = false;
}
function onDrop(event) {
    isDragging.value = false;
    const files = event.dataTransfer.files;
    handleFileChange({ target: { files } });
}
function handleFileChange(event) {
    fileError.value = "";
    const files = event.target.files;
    if (!files || files.length === 0) {
        localField.value = null;
        emit("update:modelValue", null);
        return;
    }
    let newFiles = Array.from(files);
    if (allowedFileTypesArray.value.length) {
        const invalidFiles = newFiles.filter((file) => {
            const ext = file.name.split(".").pop().toLowerCase();
            return !allowedFileTypesArray.value.includes(ext);
        });
        if (invalidFiles.length) {
            fileError.value = `The following file type(s) are not allowed: ${invalidFiles
                .map((f) => "." + f.name.split(".").pop().toLowerCase())
                .join(", ")}`;
            newFiles = newFiles.filter((file) => {
                const ext = file.name.split(".").pop().toLowerCase();
                return allowedFileTypesArray.value.includes(ext);
            });
        }
    }
    if (newFiles.length === 0) {
        localField.value = null;
        emit("update:modelValue", null);
        return;
    }
    if (props.maxUpload > 0) {
        let currentFiles = [];
        if (props.multiple && Array.isArray(localField.value)) {
            currentFiles = localField.value;
        }
        const remainingSlots = props.maxUpload - currentFiles.length;
        if (remainingSlots <= 0) {
            fileError.value =
                "You have already reached the maximum number of files allowed.";
            return;
        }
        if (newFiles.length > remainingSlots) {
            fileError.value = `You can only upload ${remainingSlots} more file(s). Only the first ${remainingSlots} file(s) were added.`;
            newFiles = newFiles.slice(0, remainingSlots);
        }
    }
    if (props.multiple) {
        const currentFiles = Array.isArray(localField.value)
            ? localField.value
            : [];
        const updatedFiles = [...currentFiles, ...newFiles];
        localField.value = updatedFiles;
        emit("update:modelValue", updatedFiles);
    } else {
        localField.value = newFiles.slice(0, 1);
        emit("update:modelValue", newFiles.slice(0, 1));
    }
}
function removeFile(index) {
    const updatedFiles = [...filesArray.value];
    updatedFiles.splice(index, 1);
    if (updatedFiles.length === 0) {
        localField.value = null;
        emit("update:modelValue", null);
    } else {
        localField.value = updatedFiles;
        emit("update:modelValue", updatedFiles);
    }
}

// Control display of label and description for certain field types
const canShowLabel = computed(() => {
    if (isConsent.value) return false;
    if (isSingleCheckbox.value) return false;
    return true;
});
const canShowDescription = computed(() => {
    if (isConsent.value) return false;
    if (isSingleCheckbox.value) return false;
    return true;
});

// Determine if the field is required based on provided rules
const isFieldRequired = computed(() => {
    if (props.rules) {
        if (typeof props.rules === "string") {
            return props.rules.includes("required");
        } else if (Array.isArray(props.rules)) {
            return props.rules.some((rule) => rule.name === "required");
        } else if (typeof props.rules === "object") {
            return !!props.rules.required;
        }
    }
    return false;
});

const input = ref(null);
defineExpose({ input });

const handleUseAiContent = (data) => {
    localField.value = data;
};
</script>

<style scoped>
.file-drop-area {
    transition: background-color 0.3s ease;
}
</style>
